// web/src/lib/curateDeckZooms.ts
import type { DetectedObject, KeySlideRegions } from "@/types/roast";

/** Hard ceiling on push-ins per deck — the balance between "deliberate emphasis" and "jerky". */
export const MAX_DECK_ZOOMS = 4;

/**
 * Deterministically select which slides actually zoom. The comedic choice already happened
 * in Phase 2 (only nominated beats reach here); this is mechanical anti-jerk enforcement:
 *  - ≤1 region per slide (highest confidence),
 *  - ≤ `max` total,
 *  - never two on dwell-sequence-adjacent slides (consecutive entries in `dwellSlideOrder`).
 * Tiebreak: confidence desc, then slideIndex asc. Confidence is only a tiebreaker — never a selector.
 */
export function curateDeckZooms(
  regionsBySlide: KeySlideRegions,
  dwellSlideOrder: number[],
  max = MAX_DECK_ZOOMS,
): KeySlideRegions {
  const seqPos = new Map<number, number>();
  dwellSlideOrder.forEach((slide, i) => seqPos.set(slide, i));

  // One candidate per slide: the highest-confidence region.
  const candidates = Object.entries(regionsBySlide)
    .map(([k, regions]) => {
      const slideIndex = Number(k);
      const region = [...regions].sort((a, b) => b.confidence - a.confidence)[0];
      return region ? { slideIndex, region } : null;
    })
    .filter((c): c is { slideIndex: number; region: DetectedObject } => c !== null)
    .sort((a, b) => b.region.confidence - a.region.confidence || a.slideIndex - b.slideIndex);

  const accepted: number[] = [];
  const out: KeySlideRegions = {};
  const dropped: number[] = [];

  for (const c of candidates) {
    if (accepted.length >= max) { dropped.push(c.slideIndex); continue; }
    const pos = seqPos.get(c.slideIndex);
    const adjacent = pos !== undefined && accepted.some((a) => {
      const ap = seqPos.get(a);
      // Both conditions required: consecutive in dwell sequence AND slide indices differ by 1.
      // Tentpoles separated by a sweep share consecutive dwell positions but have raw indices
      // far apart (|a - c.slideIndex| > 1), so they are NOT treated as adjacent.
      return ap !== undefined && Math.abs(ap - pos) === 1 && Math.abs(a - c.slideIndex) === 1;
    });
    if (adjacent) { dropped.push(c.slideIndex); continue; }
    accepted.push(c.slideIndex);
    out[c.slideIndex] = [c.region];
  }

  if (dropped.length) {
    console.info("[deck-zoom] curated zooms", { kept: accepted.sort((a, b) => a - b), dropped: dropped.sort((a, b) => a - b), max });
  }
  return out;
}
